import { localModel } from '../catalog/names.js';
import type { Units } from '../units/units.js';
import type {
  BoardData,
  Catalog,
  CatalogEntry,
  Item,
  ItemKind,
  MaterialCategory,
  MaterialExtra,
  MaterialRowSettings,
  StandSpec,
  Supplier,
} from '../model/schema.js';
import { MATERIAL_CATEGORIES } from '../model/schema.js';
import { BUILTIN_CATALOG } from '../catalog/builtin.js';
import { normalizeToken } from '../catalog/catalog.js';
import type { CellValue, Column } from '../derive/columns.js';
import { deepEqual } from '../diff/deep-equal.js';
import { channelLabel, describeStand, normalizeStandText, standOf } from '../stands/stands.js';
import { holderLabel, mountedMics, standHolder } from '../stands/holder.js';
import { matchSourceText, parseStandText } from '../stands/fromChannels.js';
import { matchMaterialName } from './reconcile.js';
import { boardMaterialConfig, sparePercentFor, sparesFor } from './config.js';
import { cableNeeds, subcoreLengths } from './cables.js';
import type { CableEstimate, LengthCount, MaterialListConfig, MaterialRow } from './types.js';
import { OWN, supplierInfo } from './supplier.js';
import { applyMaterialOverlay } from './overlay.js';
import { furnitureOf } from '../model/symbols.js';
import type { FurnitureType } from '../model/symbols.js';
import type { SupplierInfo } from './supplier.js';

type Locale = 'en' | 'de';

const KIND_CATEGORY: Record<Exclude<ItemKind, 'musician' | 'generic'>, MaterialCategory> = {
  instrument: 'backline',
  microphone: 'mics',
  di: 'dis',
  stand: 'stands',
  riser: 'risers',
  wedge: 'monitors',
  sidefill: 'monitors',
  iem: 'iem',
  monitor_mixer: 'monitors',
  speaker: 'pa',
  sub: 'pa',
  amp: 'amps',
  stagebox: 'stageboxes',
  subcore: 'subcores',
  console: 'consoles',
  power_distro: 'power',
  network_switch: 'network',
  light: 'lighting',
  truss: 'lighting',
};

/**
 * Material category of an item, null for musicians (people are no material). Generic items are
 * adapters (`props.adapter`), wireless receivers (radio port → mics) or misc.
 */
const LIST_HOLDER_KINDS = new Set<Item['kind']>(['generic', 'microphone', 'di']);

/**
 * A pure list holder (U2 / OIL-55): an object that only shows its children as a list box – no
 * brand, model or catalog entry, and not a physical kind like an instrument (a drum kit with its
 * mic list is real backline). Not material (user request 2026-10-06).
 */
export function isListHolder(
  item: Pick<Item, 'kind' | 'childDisplay' | 'brand' | 'model' | 'catalogRef'>,
): boolean {
  return (
    item.childDisplay === 'list' &&
    LIST_HOLDER_KINDS.has(item.kind) &&
    !item.catalogRef &&
    materialBrandModel(item.brand, item.model) === ''
  );
}
export function materialCategoryOf(
  item: Pick<Item, 'kind' | 'props' | 'ports'> &
    Partial<Pick<Item, 'childDisplay' | 'brand' | 'model' | 'catalogRef'>>,
): MaterialCategory | null {
  if (item.kind === 'musician') return null;
  if (isListHolder(item)) return null;
  if (item.kind !== 'generic') return KIND_CATEGORY[item.kind];
  if (item.props['adapter'] === true) return 'adapters';
  if (furnitureOf(item) !== null) return 'furniture';
  if ((item.ports ?? []).some((p) => p.signal === 'wireless')) return 'mics';
  return 'misc';
}

/** "Brand Model" without the placeholder brand "Generic"; empty if neither is known. */
export function materialBrandModel(brand: string | undefined, model: string | undefined): string {
  return [brand === 'Generic' ? undefined : brand, model]
    .filter((s) => s !== undefined && s.trim() !== '')
    .join(' ');
}

const KIND_NAME: Record<Locale, Record<Exclude<ItemKind, 'musician'>, string>> = {
  en: {
    instrument: 'Instrument',
    microphone: 'Microphone',
    di: 'DI box',
    stand: 'Stand',
    riser: 'Riser',
    wedge: 'Wedge',
    sidefill: 'Sidefill',
    iem: 'In-ear',
    monitor_mixer: 'Personal mixer',
    speaker: 'Speaker',
    sub: 'Subwoofer',
    amp: 'Amplifier',
    stagebox: 'Stage box',
    subcore: 'Sub-core',
    console: 'Console',
    power_distro: 'Power distro',
    network_switch: 'Network switch',
    light: 'Light',
    truss: 'Truss',
    generic: 'Object',
  },
  de: {
    instrument: 'Instrument',
    microphone: 'Mikrofon',
    di: 'DI-Box',
    stand: 'Stativ',
    riser: 'Podest',
    wedge: 'Monitorbox',
    sidefill: 'Sidefill',
    iem: 'In-Ear',
    monitor_mixer: 'Monitormischer',
    speaker: 'Lautsprecher',
    sub: 'Subwoofer',
    amp: 'Endstufe',
    stagebox: 'Stagebox',
    subcore: 'Sub-Multicore',
    console: 'Mischpult',
    power_distro: 'Stromverteiler',
    network_switch: 'Netzwerk-Switch',
    light: 'Scheinwerfer',
    truss: 'Traverse',
    generic: 'Objekt',
  },
};

const FURNITURE_NAME: Record<Locale, Record<FurnitureType, string>> = {
  en: {
    chair: 'Chair (no armrests)',
    chair_arms: 'Chair with armrests',
    stool: 'Stool (no backrest)',
    bar_stool: 'Bar stool',
    music_stand: 'Music stand',
  },
  de: {
    chair: 'Stuhl ohne Armlehnen',
    chair_arms: 'Stuhl mit Armlehnen',
    stool: 'Hocker ohne Lehne',
    bar_stool: 'Barhocker',
    music_stand: 'Notenpult',
  },
};

/**
 * Kinds whose label names the thing itself when there is no brand / model: backline, misc
 * objects, and mics / DIs (a mic labelled "SM57" is an SM57 – user report 2026-10-08).
 */
const LABEL_KINDS: ReadonlySet<ItemKind> = new Set(['instrument', 'generic', 'microphone', 'di']);

/** A label without the numbering of duplicates: "SM57 (2)" → "SM57". */
function baseLabel(label: string): string {
  return label.trim().replace(/\s*\(\d+\)$/u, '');
}

function size(v: number): string {
  return String(Number(v.toFixed(2)));
}

/** "4 × 4 m" for an unbranded riser with a size, else null (OIL-120). */
function genericRiserSize(item: Item, locale: Locale = 'en'): string | null {
  if (item.kind !== 'riser' || item.width === undefined || item.depth === undefined) return null;
  if (materialBrandModel(item.brand, undefined) !== '') return null;
  const n = (v: number) => (locale === 'de' ? size(v).replace('.', ',') : size(v));
  return `${n(item.width)} × ${n(item.depth)} m`;
}

/**
 * Name of an item in the material list: brand + model, stand description, for backline / misc
 * objects their label, risers by size, else the kind ("Wedge") – labels like "Vox wedge L" only
 * say where an object is, they go to the details (U71).
 */
export function materialName(item: Item, locale: Locale = 'en', units: Units = 'metric'): string {
  // unbranded chairs & co. by their type, in the UI language (U72)
  const furniture = furnitureOf(item);
  if (furniture !== null && (item.brand ?? 'Generic') === 'Generic')
    return FURNITURE_NAME[locale][furniture];
  // a generic riser by its actual size – a 2 × 2 m riser resized to 4 × 4 m is no "Riser 2x2 m"
  const riser = genericRiserSize(item, locale);
  if (riser !== null) return `${KIND_NAME[locale].riser} ${riser}`;
  const bm = materialBrandModel(
    item.brand,
    item.model === undefined ? undefined : localModel(item.model, item.brand, locale),
  );
  if (bm !== '') return bm;
  if (item.stand !== undefined) return describeStand(item.stand, locale, units);
  if (item.kind === 'musician') return item.label.trim() || item.kind;
  if (LABEL_KINDS.has(item.kind)) return baseLabel(item.label) || KIND_NAME[locale][item.kind];
  return KIND_NAME[locale][item.kind];
}

/**
 * What an item is counted as: its brand + model when known (a catalog reference can be stale –
 * an item duplicated from an MD 421 and retyped to "AE2300" keeps the MD 421 reference), else
 * its catalog reference, else its name.
 */
function itemGroupKey(item: Item, name: string): string {
  const riser = genericRiserSize(item);
  if (riser !== null) return `riser:${riser}`;
  const bm = materialBrandModel(item.brand, item.model);
  if (bm !== '') return `name:${item.kind}:${normalizeToken(bm)}`;
  if (item.catalogRef !== undefined) return `ref:${item.catalogRef}`;
  return `name:${item.kind}:${normalizeToken(name)}`;
}

interface Acc {
  rowKey: string;
  /** Extra position (U69): fixed count, spares, notes. */
  extra?: MaterialExtra;
  baseKey: string;
  supplier: SupplierInfo;
  category: MaterialCategory;
  name: string;
  count: number;
  usedBy: string[];
  sourceIds: string[];
  lengths: Map<number | null, number> | null;
  informal: boolean;
  catalogRef?: string;
}

/**
 * Rows grouped by what they are and who supplies them (U67): the own supplier keeps the plain row
 * key, other suppliers get `<key>@<supplier key>`.
 */
class Groups {
  readonly map = new Map<string, Acc>();
  constructor(
    private readonly data: BoardData,
    private readonly rows: Record<string, MaterialRowSettings>,
    private readonly ownName: string | undefined,
    private readonly locale: Locale,
  ) {}
  add(
    category: MaterialCategory,
    key: string,
    init: Pick<Acc, 'name' | 'informal'> & { catalogRef?: string | undefined },
    sourceIds: readonly string[],
    usedBy?: string,
    length?: CableEstimate,
    own?: Supplier,
  ): void {
    const baseKey = `${category}:${key}`;
    const supplier = supplierInfo(
      this.data,
      own ?? this.rows[baseKey]?.supplier ?? OWN,
      this.ownName,
      this.locale,
    );
    const rowKey = supplier.key === 'own' ? baseKey : `${baseKey}@${supplier.key}`;
    let acc = this.map.get(rowKey);
    if (acc === undefined) {
      acc = {
        rowKey,
        baseKey,
        supplier,
        category,
        name: init.name,
        count: 0,
        usedBy: [],
        sourceIds: [],
        lengths: null,
        informal: init.informal,
      };
      if (init.catalogRef !== undefined) acc.catalogRef = init.catalogRef;
      this.map.set(rowKey, acc);
    } else if (acc.catalogRef !== init.catalogRef) {
      // items of one row disagree on the catalog entry: none of them stands for the row
      delete acc.catalogRef;
    }
    acc.count += 1;
    acc.sourceIds.push(...sourceIds);
    this.note(acc, usedBy, length);
  }
  addExtra(extra: MaterialExtra): void {
    const rowKey = `extra:${extra.id}`;
    this.map.set(rowKey, {
      rowKey,
      baseKey: rowKey,
      supplier: supplierInfo(this.data, extra.supplier ?? OWN, this.ownName, this.locale),
      category: extra.category,
      name: extra.name,
      count: extra.count,
      usedBy: [],
      sourceIds: [],
      lengths: null,
      informal: false,
      extra,
    });
  }
  private note(acc: Acc, usedBy?: string, length?: CableEstimate): void {
    if (usedBy !== undefined && usedBy !== acc.name) acc.usedBy.push(usedBy);
    if (length !== undefined) {
      acc.lengths ??= new Map();
      acc.lengths.set(length.length, (acc.lengths.get(length.length) ?? 0) + 1);
    }
  }
}

/**
 * Who an item is for: the names of the channels it feeds ("Sax1" rather than its label
 * "MD 421 (1)", OIL-123), else its label.
 */
function whoOf(data: BoardData, item: Item): string {
  const channels = Object.values(data.channels)
    .filter((c) => c.sourceItemId === item.id && c.name.trim() !== '')
    .sort((a, b) => a.number - b.number)
    .map((c) => c.name.trim());
  return channels.length > 0 ? channels.join(' / ') : item.label.trim();
}

function usedByOf(data: BoardData, item: Item): string | undefined {
  if (item.kind === 'stand') {
    const mounted = Object.values(data.items)
      .filter((i) => i.mountedOn === item.id)
      .map((i) => whoOf(data, i) || i.model || i.kind);
    return mounted.length > 0 ? mounted.sort((a, b) => a.localeCompare(b)).join(', ') : undefined;
  }
  const who = whoOf(data, item);
  return who === '' ? undefined : who;
}

/**
 * Items by the first channel they feed – a stand by the first channel of what is mounted on it –
 * so "for:" reads in channel order ("Piano L, Piano R, OH", OIL-224); the rest keep their order.
 */
function inChannelOrder(data: BoardData): Item[] {
  const first = new Map<string, number>();
  for (const c of Object.values(data.channels)) {
    const id = c.sourceItemId;
    if (id !== undefined && c.number < (first.get(id) ?? Infinity)) first.set(id, c.number);
  }
  const rank = (item: Item): number =>
    item.kind === 'stand'
      ? Math.min(
          Infinity,
          ...Object.values(data.items)
            .filter((i) => i.mountedOn === item.id)
            .map((i) => first.get(i.id) ?? Infinity),
        )
      : (first.get(item.id) ?? Infinity);
  const items = Object.values(data.items);
  const ranks = new Map(items.map((i) => [i.id, rank(i)]));
  return items.sort((a, b) => ranks.get(a.id)! - ranks.get(b.id)!);
}

function addItems(data: BoardData, groups: Groups, config: MaterialListConfig, locale: Locale) {
  const subcores = subcoreLengths(data, config);
  for (const item of inChannelOrder(data)) {
    const category = materialCategoryOf(item);
    if (category === null) continue;
    const name = materialName(item, locale, data.meta.units);
    groups.add(
      category,
      // keys are locale-independent: merges and settings must work in every language
      itemGroupKey(item, materialName(item, 'en')),
      { name, informal: false, catalogRef: item.catalogRef },
      [item.id],
      usedByOf(data, item),
      item.kind === 'subcore'
        ? (subcores.get(item.id) ?? { estimated: null, length: null })
        : undefined,
      item.supplier,
    );
  }
}

/**
 * Holders for several mics on one stand (U70): a stereo bar, mic bar or double clamp per stand
 * that needs one, supplied with its stand; "for:" lists the mics on it.
 */
function addHolders(data: BoardData, groups: Groups, locale: Locale) {
  for (const stand of Object.values(data.items)) {
    if (stand.kind !== 'stand') continue;
    const holder = standHolder(data, stand);
    if (holder === null) continue;
    const mics = mountedMics(data, stand.id)
      .map((i) => i.label.trim() || i.model || i.kind)
      .sort((a, b) => a.localeCompare(b));
    groups.add(
      'stands',
      `holder:${holder}`,
      { name: holderLabel(holder, locale), informal: false },
      [stand.id],
      mics.length > 0 ? mics.join(', ') : undefined,
      undefined,
      stand.supplier,
    );
  }
}

function entryName(entry: CatalogEntry): string {
  return materialBrandModel(entry.brand, entry.model);
}

/** Catalog stand entry with exactly this spec (informal texts map to the built-in specs). */
function standEntry(entries: readonly CatalogEntry[], spec: StandSpec): CatalogEntry | undefined {
  return entries.find(
    (e) => e.kind === 'stand' && e.stand !== undefined && deepEqual(e.stand, spec),
  );
}

const DI_TEXT = /(?<![\p{L}\p{N}])di(?![\p{L}])/iu;

/** Informal channel sources (free text, no item) and informal stand texts. */
function addInformal(
  data: BoardData,
  groups: Groups,
  entries: readonly CatalogEntry[],
  locale: Locale,
) {
  const channels = Object.values(data.channels).sort((a, b) => a.number - b.number);
  const sources = entries.filter((e) => e.kind !== 'stand');
  for (const ch of channels) {
    const source = data.items[ch.sourceItemId as string];
    const user = channelLabel(ch);
    const text = normalizeStandText(ch.sourceText ?? '');
    if (source === undefined && text !== '') {
      const entry = matchSourceText(entries, text) ?? matchMaterialName(sources, text);
      if (entry !== undefined) {
        const category =
          materialCategoryOf({ kind: entry.kind, props: entry.props ?? {}, ports: entry.ports }) ??
          'misc';
        groups.add(
          category,
          `informal:ref:${entry.ref}`,
          { name: entryName(entry), informal: true, catalogRef: entry.ref },
          [ch.id],
          user,
        );
      } else if (DI_TEXT.test(text)) {
        groups.add('dis', 'informal:di', { name: 'DI', informal: true }, [ch.id], user);
      } else {
        groups.add(
          'mics',
          `informal:text:${text.toLowerCase()}`,
          { name: text, informal: true },
          [ch.id],
          user,
        );
      }
    }
    const standTextValue = normalizeStandText(ch.stand ?? '');
    if (standTextValue === '' || standOf(data, source) !== undefined) continue;
    const spec = parseStandText(standTextValue);
    const entry = spec === null ? undefined : standEntry(entries, spec);
    const name =
      entry !== undefined
        ? entryName(entry)
        : spec !== null
          ? describeStand(spec, locale, data.meta.units)
          : standTextValue;
    groups.add(
      'stands',
      `informal:${entry !== undefined ? `ref:${entry.ref}` : `text:${name.toLowerCase()}`}`,
      { name, informal: true, catalogRef: entry?.ref },
      [ch.id],
      user,
    );
  }
}

/** Extra positions without an object (U69); each is its own row `extra:<id>`. */
function addExtras(data: BoardData, groups: Groups) {
  for (const extra of data.meta.materialList?.extras ?? []) groups.addExtra(extra);
}

function addCables(data: BoardData, groups: Groups, config: MaterialListConfig, locale: Locale) {
  for (const need of cableNeeds(data, config, locale)) {
    groups.add(
      need.category,
      `cable:${normalizeToken(need.name)}`,
      { name: need.name, informal: false },
      need.sourceIds,
      undefined,
      need,
    );
  }
}

/** "3 m" / "1.5 m" with at most two decimals; German "1,5 m" (OIL-200). */
function metres(n: number, locale: Locale): string {
  const text = String(Number(n.toFixed(2)));
  return `${locale === 'de' ? text.replace('.', ',') : text} m`;
}

/** Length breakdown "8× 3 m, 1× 10 m, 2× ?" (ascending, unknown last). */
export function formatLengths(lengths: readonly LengthCount[], locale: Locale = 'en'): string {
  return lengths
    .map((l) => `${l.count}× ${l.length === null ? '?' : metres(l.length, locale)}`)
    .join(', ');
}

function sortedLengths(map: Map<number | null, number>): LengthCount[] {
  return [...map.entries()]
    .map(([length, count]) => ({ length, count }))
    .sort((a, b) => (a.length ?? Infinity) - (b.length ?? Infinity));
}

const FOR: Record<Locale, string> = { en: 'for', de: 'für' };

/** Why a stereo bar / mic bar / double clamp is listed (U70; it is never placed, OIL-146). */
const HOLDER_NOTE: Record<Locale, string> = {
  en: 'automatic: several mics on one stand',
  de: 'automatisch: mehrere Mikros an einem Stativ',
};

/**
 * Material list of a board (decision U11): every modelled item (musicians excluded), informal
 * channel sources / stand texts (rows flagged `informal`) and every needed cable with estimated
 * lengths, aggregated by category and model, with spares. Disabled categories are left out;
 * pass `includeDisabled` for the full list. Rows are ordered by category, formal before informal,
 * then by name.
 */
export function materialList(
  data: BoardData,
  config: MaterialListConfig = boardMaterialConfig(data),
  catalogs: readonly Catalog[] = [BUILTIN_CATALOG],
  locale: Locale = 'en',
  opts: { includeDisabled?: boolean; ownName?: string } = {},
): MaterialRow[] {
  const entries = catalogs.flatMap((c) => c.entries);
  const groups = new Groups(data, data.meta.materialList?.rows ?? {}, opts.ownName, locale);
  addItems(data, groups, config, locale);
  addInformal(data, groups, entries, locale);
  addHolders(data, groups, locale);
  addCables(data, groups, config, locale);
  addExtras(data, groups);
  const rowSettings = data.meta.materialList?.rows ?? {};
  const order = (c: MaterialCategory) => MATERIAL_CATEGORIES.indexOf(c);
  const generated = [...groups.map.values()]
    .filter((acc) => opts.includeDisabled === true || config.categories[acc.category])
    .map((acc): MaterialRow => {
      // per-row spares (U69): an extra's own, else the row setting, else the category percentage
      const fixed = acc.extra !== undefined ? acc.extra.spares : rowSettings[acc.rowKey]?.spares;
      const spares = fixed ?? sparesFor(acc.count, sparePercentFor(config, acc.category));
      const usedBy = [...acc.usedBy];
      const lengths = acc.lengths === null ? undefined : sortedLengths(acc.lengths);
      const details = [
        acc.extra?.notes ?? '',
        acc.baseKey.includes('holder:') ? HOLDER_NOTE[locale] : '',
        usedBy.length > 0 ? `${FOR[locale]}: ${usedBy.join(', ')}` : '',
        lengths === undefined ? '' : formatLengths(lengths, locale),
      ]
        .filter((s) => s !== '')
        .join(' · ');
      const row: MaterialRow = {
        rowKey: acc.rowKey,
        baseKey: acc.baseKey,
        supplier: acc.supplier,
        category: acc.category,
        name: acc.name,
        count: acc.count,
        spares,
        total: acc.count + spares,
        details,
        usedBy,
        informal: acc.informal,
        sourceIds: acc.sourceIds,
      };
      if (lengths !== undefined) row.lengths = lengths;
      if (fixed !== undefined) row.sparesFixed = true;
      if (acc.extra !== undefined) row.extraId = acc.extra.id;
      if (acc.catalogRef !== undefined) row.catalogRef = acc.catalogRef;
      return row;
    });
  // renamed and merged positions (U71)
  return applyMaterialOverlay(generated, data.meta.materialList, locale).sort(
    (a, b) =>
      order(a.category) - order(b.category) ||
      Number(a.informal) - Number(b.informal) ||
      Number(a.extraId !== undefined) - Number(b.extraId !== undefined) ||
      a.name.localeCompare(b.name) ||
      Number(a.supplier.key !== 'own') - Number(b.supplier.key !== 'own') ||
      a.supplier.label.localeCompare(b.supplier.label) ||
      a.rowKey.localeCompare(b.rowKey),
  );
}

/**
 * Export columns of the material list (CSV / PDF). `categoryLabel` translates the category keys
 * (default: the key itself).
 */
export function materialColumns(
  categoryLabel: (c: MaterialCategory) => string = (c) => c,
): Column<MaterialRow>[] {
  const col = (key: string, value: (r: MaterialRow) => CellValue): Column<MaterialRow> => ({
    key,
    labelKey: `columns.materials.${key}`,
    value,
  });
  return [
    col('category', (r) => categoryLabel(r.category)),
    col('count', (r) => r.count),
    col('spares', (r) => r.spares),
    col('total', (r) => r.total),
    col('name', (r) => r.name),
    col('details', (r) => r.details),
    col('supplier', (r) => r.supplier.label),
    col('informal', (r) => r.informal),
  ];
}

/** Distinct suppliers of the rows (U67): own first, then house, then by name. */
export function materialSuppliers(rows: readonly MaterialRow[]): SupplierInfo[] {
  const seen = new Map<string, SupplierInfo>();
  for (const r of rows) if (!seen.has(r.supplier.key)) seen.set(r.supplier.key, r.supplier);
  const rank = (s: SupplierInfo) => (s.kind === 'own' ? 0 : s.kind === 'house' ? 1 : 2);
  return [...seen.values()].sort(
    (a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label) || a.key.localeCompare(b.key),
  );
}

/** The rows of one supplier (its key), or all rows for `null` – one list per supplier (U67). */
export function rowsOfSupplier(
  rows: readonly MaterialRow[],
  supplierKey: string | null,
): MaterialRow[] {
  return supplierKey === null ? [...rows] : rows.filter((r) => r.supplier.key === supplierKey);
}
